import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { AdSupplier } from '@magnetar/admin-api-interfaces'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useViewMode } from '@/stores/viewMode'
import { useViewModeActions } from '@/hooks/useViewModeActions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { adSupplierGroupService } from '@/services/ad_supplier_group_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

export const columns: ColumnDef<AdSupplier>[] = [
  {
    accessorKey: 'supplier_id',
    header: 'ID',
    cell: ({ row }) => {
      const id: number = row.getValue('supplier_id')
      return (
        <Badge variant="outline" className="font-medium">
          {id}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'supplier_name',
    header: '供應商名稱',
  },
  {
    accessorKey: 'manager',
    header: '負責人',
  },
  {
    accessorKey: 'phone',
    header: '電話',
  },
  {
    accessorKey: 'is_active',
    header: '狀態',
    cell: ({ row }) => {
      const is_active: boolean = row.getValue('is_active')
      return (
        <Badge variant={is_active ? 'default' : 'secondary'}>{is_active ? '啟用' : '停用'}</Badge>
      )
    },
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const supplier = row.original
      const { id: groupId } = useViewMode()
      const { setAdMode } = useViewModeActions()
      const { token } = useAuth()
      const [isRemoving, setIsRemoving] = useState(false)

      const handleEdit = () => {
        setAdMode(supplier.supplier_id)
      }

      const handleRemove = async () => {
        if (!groupId || !token) return
        setIsRemoving(true)
        try {
          const result = await adSupplierGroupService.removeSupplier(groupId, supplier.supplier_id, token)
          if (result.success) {
            toast.success('已將供應商從集團中移除')
            // Reload data
            const meta = table.options.meta as { reloadData?: () => void }
            meta?.reloadData?.()
          } else {
            toast.error('移除供應商失敗')
          }
        } catch {
          toast.error('移除供應商失敗')
        } finally {
          setIsRemoving(false)
        }
      }

      return (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleEdit}>
            編輯
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isRemoving}>
                移除
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>確定要移除此供應商？</AlertDialogTitle>
                <AlertDialogDescription>
                  此操作會將「{supplier.supplier_name}」從集團中移除，但不會刪除供應商資料。
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>取消</AlertDialogCancel>
                <AlertDialogAction onClick={handleRemove}>確定移除</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )
    },
  },
]
